import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

const col1 = categories.slice(0, 4);
const col2 = categories.slice(4, 8);
const col3 = categories.slice(8, 12);

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot?.value.trim()) {
      setSubmitted(true);
      return;
    }
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      await fetch("https://readdy.ai/api/form/d92h2gnefssur58kqvv0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
    } catch {
      // silently handle
    }
    setSubmitting(false);
    setSubmitted(true);
    setEmail("");
  };

  return (
    <footer className="bg-foreground-900 text-background-50 pt-16 pb-8 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        {/* Top */}
        <div className="flex flex-col md:flex-row items-start justify-between mb-12 gap-6">
          <div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-xl font-heading font-bold hover:text-primary-400 transition-colors"
            >
              GTBuy Sheet
            </a>
            <p className="text-sm text-foreground-300 mt-2 max-w-xs">
              The smart spreadsheet system for global product sourcing
            </p>
          </div>
          <div className="text-sm text-foreground-400">
            &copy; {new Date().getFullYear()} GTBuy Sheet. All rights reserved.
          </div>
        </div>

        {/* Category Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          <div>
            <p className="text-sm font-semibold text-background-50/80 mb-3">Categories</p>
            <ul className="space-y-2">
              {col1.map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-400 hover:text-primary-400 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-background-50/80 mb-3">&nbsp;</p>
            <ul className="space-y-2">
              {col2.map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-400 hover:text-primary-400 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-background-50/80 mb-3">&nbsp;</p>
            <ul className="space-y-2">
              {col3.map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-400 hover:text-primary-400 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-background-50/80 mb-3">Quick Links</p>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/article/gtbuy-spreadsheet-guide"
                  className="text-sm text-foreground-400 hover:text-primary-400 transition-colors whitespace-nowrap"
                >
                  GTBuy Sheet Guide
                </Link>
              </li>
              <li>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-400 hover:text-primary-400 transition-colors whitespace-nowrap"
                >
                  Main Platform
                </a>
              </li>
            </ul>

            {/* Newsletter */}
            <div className="mt-6">
              <p className="text-sm font-semibold text-background-50/80 mb-2">Stay Updated</p>
              {submitted ? (
                <p className="text-xs text-accent-400">Thank you for subscribing!</p>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="flex gap-2"
                >
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="honeypot-field"
                  />
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    required
                    className="flex-1 px-3 py-2 rounded-md bg-foreground-800 border border-foreground-700 text-sm text-background-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all disabled:opacity-50 whitespace-nowrap cursor-pointer"
                  >
                    {submitting ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <i className="ri-arrow-right-line"></i>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-foreground-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            Secure &bull; GDPR Compliant &bull; Encrypted Workflow
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors"
            >
              Privacy
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors"
            >
              Terms
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-xs text-foreground-500 hover:text-foreground-300 transition-colors"
            >
              Contact
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}